import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { enterpriseApiClient } from '../../client';
import { IndyODataApi } from '../../openapi';
import { db } from '@db/database';
import type { ServiceAgreementChargeTypeViewModelODataResponseViewModel } from '../../openapi';
import type { ServiceAgreementChargeTypesRequest } from '../types';

export const useServiceAgreementChargeTypes = (
  searchParams: ServiceAgreementChargeTypesRequest = {},
  enabled = true
) => {
  const indyODataApi = new IndyODataApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  const queryKey = useMemo(() => {
    const { filter, select, orderBy, top, skip, includeCount } = searchParams;
    return [
      'service-agreement-charge-types',
      {
        filter,
        select: select?.join(','),
        orderBy,
        top,
        skip,
        includeCount
      }
    ];
  }, [searchParams]);

  return useQuery<
    ServiceAgreementChargeTypeViewModelODataResponseViewModel,
    Error
  >({
    queryKey,
    queryFn: async () => {
      const { filter, select, orderBy, top, skip, includeCount } = searchParams;

      try {
        const response =
          await indyODataApi.apiOdataServiceAgreementChargeTypesGet({
            $filter: filter,
            $select: select?.join(','),
            $orderby: orderBy,
            $top: top,
            $skip: skip,
            $count: includeCount
          });
        return response.data;
      } catch (error) {
        if (!navigator.onLine) {
          const cachedTypes = await db.cachedChargeTypes.toArray();
          return {
            value: cachedTypes
          } as ServiceAgreementChargeTypeViewModelODataResponseViewModel;
        }
        throw error;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
};
